/**
 * ペルソナの「知りたいこと」（観点）を 1 件ずつ操作する区画。
 *
 * 本体の編集フォーム（名前・説明ほか）とは別の口を使う。観点は採点の単位で、
 * 有効・無効を切り替えるだけでカバレッジの母数が変わるため、名前の変更と
 * 同じ保存ボタンに畳むと「何をしたのか」が監査から読めなくなる。
 *
 * どの操作も `expectedVersion` を添えて送る。ペルソナの版は 1 件の操作ごとに
 * 進むので、送ったあとは必ず読み直す（続けて送ると CONFLICT になる）。
 */

import { useState } from "react";
import { useMutation } from "urql";
import {
  AddPersonaQuestionDocument,
  Button,
  Card,
  RemovePersonaQuestionDocument,
  Tag,
  UpdatePersonaQuestionDocument,
  type PersonaFieldsFragment,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

import { type PersonaUserError } from "./PersonaForm";

export function PersonaQuestionsCard({
  persona,
  onChanged,
}: {
  persona: PersonaFieldsFragment;
  onChanged: () => void;
}) {
  const [addState, addQuestion] = useMutation(AddPersonaQuestionDocument);
  const [updateState, updateQuestion] = useMutation(
    UpdatePersonaQuestionDocument,
  );
  const [removeState, removeQuestion] = useMutation(
    RemovePersonaQuestionDocument,
  );
  const [userErrors, setUserErrors] = useState<PersonaUserError[]>([]);
  const [question, setQuestion] = useState("");
  const [note, setNote] = useState("");
  // 注記の下書き。読み直すと入力欄も最新から始まるように、鍵に id を使う
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});

  const busy =
    addState.fetching || updateState.fetching || removeState.fetching;

  const handle = (
    promise: Promise<{
      error?: unknown;
      data?: { [key: string]: { userErrors: readonly PersonaUserError[] } };
    }>,
    key: string,
    after: () => void,
  ): void => {
    void promise.then((result) => {
      if (result.error !== undefined) {
        setUserErrors([
          {
            field: null,
            code: "INTERNAL_ERROR",
            message: `保存できませんでした: ${String(result.error)}`,
          },
        ]);
        return;
      }
      const payload = result.data?.[key];
      if (payload === undefined) {
        return;
      }
      if (payload.userErrors.length > 0) {
        setUserErrors([...payload.userErrors]);
        return;
      }
      setUserErrors([]);
      after();
      onChanged();
    });
  };

  const conflicted = userErrors.some((error) => error.code === "CONFLICT");
  const sorted = [...persona.questions].sort((a, b) => a.order - b.order);

  return (
    <Card
      title="知りたいこと（観点）"
      extra={<HelpIcon slug="personas/questions" />}
      note="無効にした観点は採点の対象から外れます。過去に数えた結果は残りますが、次に数え直したときの割合はこの一覧で決まります。"
    >
      {conflicted ? (
        <div className="vd-notice" role="alert">
          <p style={{ marginTop: 0 }}>
            このペルソナは他の画面で更新されています。最新を読み込んでから
            やり直してください。
          </p>
          <Button
            onClick={() => {
              setUserErrors([]);
              onChanged();
            }}
          >
            最新を読み込む
          </Button>
        </div>
      ) : userErrors.length === 0 ? null : (
        <div className="vd-notice vd-notice--error" role="alert">
          {userErrors.map((error) => (
            <p key={`${error.code}-${error.message}`} style={{ margin: 0 }}>
              {error.message}
            </p>
          ))}
        </div>
      )}

      {sorted.length === 0 ? (
        <p className="vd-card-note" style={{ marginTop: 0 }}>
          まだ観点がありません。1 件でも足すと、その観点で商品を採点できます。
        </p>
      ) : (
        <ul className="vd-stack" style={{ listStyle: "none", padding: 0 }}>
          {sorted.map((item) => {
            const draft = noteDrafts[item.id] ?? item.note ?? "";
            return (
              <li key={item.id} className="vd-field">
                <div className="vd-row">
                  <span>{item.question}</span>
                  {item.enabled ? (
                    <Tag tone="ok">有効</Tag>
                  ) : (
                    <Tag>無効（採点しません）</Tag>
                  )}
                  {item.fromTemplateId === null ? null : (
                    <Tag tone="info">ひな形から</Tag>
                  )}
                </div>
                <div className="vd-row">
                  <Button
                    disabled={busy}
                    aria-label={`「${item.question}」を${
                      item.enabled ? "無効にする" : "有効にする"
                    }`}
                    onClick={() =>
                      handle(
                        updateQuestion({
                          input: {
                            personaId: persona.id,
                            questionId: item.id,
                            enabled: !item.enabled,
                            expectedVersion: persona.version,
                          },
                        }),
                        "updatePersonaQuestion",
                        () => undefined,
                      )
                    }
                  >
                    {item.enabled ? "無効にする" : "有効にする"}
                  </Button>
                  <input
                    className="vd-input"
                    aria-label={`「${item.question}」の注記`}
                    placeholder="注記（なぜこの観点を見るのか）"
                    value={draft}
                    onChange={(event) =>
                      setNoteDrafts((current) => ({
                        ...current,
                        [item.id]: event.target.value,
                      }))
                    }
                  />
                  <Button
                    disabled={busy || draft === (item.note ?? "")}
                    aria-label={`「${item.question}」の注記を保存する`}
                    onClick={() =>
                      handle(
                        updateQuestion({
                          input: {
                            personaId: persona.id,
                            questionId: item.id,
                            note: draft,
                            expectedVersion: persona.version,
                          },
                        }),
                        "updatePersonaQuestion",
                        () =>
                          setNoteDrafts((current) => {
                            const next = { ...current };
                            delete next[item.id];
                            return next;
                          }),
                      )
                    }
                  >
                    注記を保存する
                  </Button>
                  <Button
                    disabled={busy}
                    aria-label={`「${item.question}」を削除する`}
                    onClick={() =>
                      handle(
                        removeQuestion({
                          input: {
                            personaId: persona.id,
                            questionId: item.id,
                            expectedVersion: persona.version,
                          },
                        }),
                        "removePersonaQuestion",
                        () => undefined,
                      )
                    }
                  >
                    削除する
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="vd-field" style={{ marginTop: 12 }}>
        <label htmlFor="persona-question-new">観点を足す</label>
        <span className="vd-field-hint">
          1 文で。例: 値段はいくらか / どんな商品か
        </span>
        <input
          id="persona-question-new"
          className="vd-input"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
        />
        <input
          className="vd-input"
          aria-label="足す観点の注記"
          placeholder="注記（任意）"
          value={note}
          onChange={(event) => setNote(event.target.value)}
        />
        <div className="vd-row">
          <Button
            variant="primary"
            disabled={busy || question.trim() === ""}
            onClick={() =>
              handle(
                addQuestion({
                  input: {
                    personaId: persona.id,
                    question: question.trim(),
                    note: note.trim() === "" ? null : note.trim(),
                    expectedVersion: persona.version,
                  },
                }),
                "addPersonaQuestion",
                () => {
                  setQuestion("");
                  setNote("");
                },
              )
            }
          >
            観点を足す
          </Button>
        </div>
      </div>
    </Card>
  );
}
